/**
 * @name ModalAdicionarRestricao
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal para adicionar nova restrição alimentar
 * @param {ModalAdicionarRestricaoProps} props - Props do modal
 * @returns {JSX.Element} Modal de adição de restrição
 * @package src/components/pages/gestao-financeira/dependentes/restricoes/ModalAdicionarRestricao.tsx
 */

import { Restaurant as RestaurantIcon, Warning as WarningIcon } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { IProdutos } from '../../../../../types/tables/IProdutos';
import { IRestricoesAlimentares } from '../../../../../types/tables/IRestricoesAlimentares';

export interface ModalAdicionarRestricaoProps {
     open: boolean;
     onClose: () => void;
     onConfirm: (produtoId: number, observacoes?: string) => Promise<void>;
     produtosDisponiveis: IProdutos[];
     restricoesExistentes: IRestricoesAlimentares[];
     loading: boolean;
}

const ModalAdicionarRestricao: React.FC<ModalAdicionarRestricaoProps> = ({ open, onClose, onConfirm, produtosDisponiveis, restricoesExistentes, loading }) => {
     const [produtoSelecionado, setProdutoSelecionado] = useState<IProdutos | null>(null);
     const [observacoes, setObservacoes] = useState('');
     const [erro, setErro] = useState<string | null>(null);

     // Filtrar produtos que já não possuem restrição
     const produtosFiltrados = useMemo(() => {
          const idsRestritos = restricoesExistentes.map((r) => r.fk_id_produto);
          return produtosDisponiveis.filter((p) => !idsRestritos.includes(p.id));
     }, [produtosDisponiveis, restricoesExistentes]);

     // Agrupar produtos por grupo/categoria (não utilizado, removido)

     const handleConfirm = async () => {
          setErro(null);

          if (!produtoSelecionado) {
               setErro('Selecione um produto');
               return;
          }

          await onConfirm(produtoSelecionado.id, observacoes || undefined);

          // Limpar campos após sucesso
          setProdutoSelecionado(null);
          setObservacoes('');
     };

     const handleClose = () => {
          setProdutoSelecionado(null);
          setObservacoes('');
          setErro(null);
          onClose();
     };

     return (
          <Dialog
               open={open}
               onClose={handleClose}
               maxWidth='sm'
               fullWidth
          >
               <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <RestaurantIcon color='primary' />
                         <Typography variant='h6'>Adicionar Restrição Alimentar</Typography>
                    </Box>
               </DialogTitle>

               <DialogContent>
                    <Box sx={{ mt: 2 }}>
                         <Alert
                              severity='warning'
                              icon={<WarningIcon />}
                              sx={{ mb: 3 }}
                         >
                              Produtos com restrição serão bloqueados automaticamente no totem, impedindo que o aluno faça a compra.
                         </Alert>

                         <FormControl
                              fullWidth
                              error={!!erro}
                              sx={{ mb: 2 }}
                         >
                              <Autocomplete
                                   options={produtosFiltrados}
                                   groupBy={(option) => {
                                        const grupo = option.fk_id_grupo ? `Grupo ${option.fk_id_grupo}` : 'Sem Grupo';
                                        return grupo;
                                   }}
                                   getOptionLabel={(option) => option.descricao}
                                   value={produtoSelecionado}
                                   onChange={(_, newValue) => {
                                        setProdutoSelecionado(newValue);
                                        setErro(null);
                                   }}
                                   renderInput={(params) => (
                                        <TextField
                                             {...params}
                                             label='Produto'
                                             placeholder='Selecione o produto a ser restrito'
                                             error={!!erro}
                                        />
                                   )}
                                   renderOption={(props, option) => (
                                        <Box
                                             component='li'
                                             {...props}
                                        >
                                             <Box sx={{ width: '100%' }}>
                                                  <Typography variant='body1'>{option.descricao}</Typography>
                                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                       {option.valor && (
                                                            <Chip
                                                                 label={`R$ ${option.valor.toFixed(2)}`}
                                                                 size='small'
                                                                 variant='outlined'
                                                            />
                                                       )}
                                                  </Box>
                                             </Box>
                                        </Box>
                                   )}
                                   noOptionsText='Nenhum produto disponível'
                                   disabled={loading}
                              />
                              {erro && <FormHelperText>{erro}</FormHelperText>}
                         </FormControl>

                         <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label='Observações (opcional)'
                              placeholder='Ex: Alergia severa, intolerância, etc.'
                              value={observacoes}
                              onChange={(e) => setObservacoes(e.target.value)}
                              disabled={loading}
                              helperText='Adicione detalhes sobre a restrição para maior controle'
                         />

                         {produtosFiltrados.length === 0 && (
                              <Alert
                                   severity='info'
                                   sx={{ mt: 2 }}
                              >
                                   Todos os produtos disponíveis já possuem restrição cadastrada.
                              </Alert>
                         )}
                    </Box>
               </DialogContent>

               <DialogActions>
                    <Button
                         onClick={handleClose}
                         disabled={loading}
                    >
                         Cancelar
                    </Button>
                    <Button
                         onClick={handleConfirm}
                         variant='contained'
                         disabled={loading || !produtoSelecionado}
                    >
                         {loading ? 'Adicionando...' : 'Adicionar Restrição'}
                    </Button>
               </DialogActions>
          </Dialog>
     );
};

export default ModalAdicionarRestricao;
